import React, { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import SafeImage from './SafeImage';
import { Vertical } from '../data/verticals';
import { verticalLogos } from '../data/media';
import { useIsMobile } from '../hooks/useIsMobile';

interface BookshelfProps {
  items: Vertical[];
  images: Record<string, string>;
  icons?: Record<string, React.ElementType>;
}

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// verticalLogos is keyed by code, but casing can differ from verticals.ts's
// own `code` field (e.g. "Academy" vs "ACADEMY") — match case-insensitively.
const logoFor = (code: string): string | undefined =>
  verticalLogos[code] ?? Object.entries(verticalLogos).find(([k]) => k.toUpperCase() === code.toUpperCase())?.[1];

// Explicit per-vertical domains, keyed by `id` — TLDs vary (.com / .in / .co.in)
// so a guessed pattern doesn't work; these are placeholders, swap in the real
// domains whenever you have them. `v.website` on the vertical itself (in
// verticals.ts) still wins if it's set, so you can override per-vertical there
// instead of editing this map.
const VERTICAL_WEBSITES: Record<string, string> = {
  bms: 'reachmedinscansandlabs.com',
  rmc: 'https://reachmultispecialityclinic.com/',
  rhms: 'https://reachhomecare.in/',
  rp: 'reachpharma.in',
  rsp: 'reachsurgicals.co.in',
  rsl: 'reachscansandlabs.com',
  academy: 'reachacademy.co.in',
  impex: 'https://reachimpex.co/',
  gardens: 'reachgardens.in',
  rsa: 'reachsportsacademy.co.in',
};

const urlFor = (v: Vertical): string => {
  const domain = v.website || VERTICAL_WEBSITES[v.id];
  if (!domain) return '#';
  return domain.startsWith('http') ? domain : `https://www.${domain}`;
};

/** Mobile-only layout: cards stacked one per row, already at full "expanded"
 * width instead of a shrunk spine (hover-to-fan doesn't exist on touch, so
 * a spine has no way to open there). Tapping a card is a two-step flow:
 * first tap reveals the description in place, a second tap on "Learn More"
 * (which stops propagation so it doesn't just re-toggle the card) is what
 * actually navigates out. */
const MobileBookshelf: React.FC<{ items: Vertical[]; images: Record<string, string> }> = ({ items, images }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {items.map(v => {
        const logo = logoFor(v.code);
        const isOpen = expandedId === v.id;
        return (
          <div
            key={v.id}
            onClick={() => setExpandedId(isOpen ? null : v.id)}
            role="button"
            tabIndex={0}
            style={{
              position: 'relative', borderRadius: 20, overflow: 'hidden', cursor: 'pointer',
              // Explicit height (not minHeight) — SafeImage sizes itself with
              // a percentage height internally, which only resolves against
              // a parent with a real computed height. minHeight alone left
              // that percentage unresolved, which is what showed as blank
              // white space instead of the photo growing with the card.
              height: isOpen ? 300 : 190, background: v.color,
              transition: 'height 0.35s cubic-bezier(.22,1,.36,1)',
              boxShadow: '0 10px 30px rgba(0,20,22,0.18)',
            }}
          >
            <SafeImage src={images[v.id]} alt={v.name} />
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(180deg, rgba(0,10,12,0.1) 0%, ${v.color}4d 45%, rgba(0,10,12,0.92) 100%)`,
            }} />

            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {logo && (
                  <img src={logo} alt={`${v.name} logo`} style={{ width: 42, height: 36, objectFit: 'contain', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#fff',
                    background: 'rgba(255,255,255,0.18)', padding: '3px 9px', borderRadius: 6,
                    letterSpacing: '0.5px', fontFamily: 'Poppins, sans-serif', marginBottom: 4,
                  }}>{v.code}</span>
                  <h3 style={{
                    fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 16.5, color: '#fff', margin: 0,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{v.name}</h3>
                </div>
                <ChevronDown
                  size={20} color="#fff" style={{ flexShrink: 0, transition: 'transform 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'none' }}
                />
              </div>

              {isOpen && (
                <>
                  <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.55, margin: '2px 0 4px' }}>
                    {v.description.slice(0, 220)}
                  </p>
                  <a
                    href={urlFor(v)} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                      alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#fff', color: v.color, fontWeight: 700, fontSize: 13,
                      padding: '9px 16px', borderRadius: 9, fontFamily: 'Poppins, sans-serif', textDecoration: 'none',
                    }}
                  >
                    Learn More <ArrowRight size={14} />
                  </a>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Renders verticals as three "shelves" of book spines that fan open on hover
 * (desktop/tablet-with-hover only — see MobileBookshelf above for touch). */
const Bookshelf: React.FC<BookshelfProps> = ({ items, images }) => {
  const isMobile = useIsMobile();
  const shelves = chunk(items, 2);

  if (isMobile) return <MobileBookshelf items={items} images={images} />;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 20,
      position: 'relative', left: '50%', width: '100vw', marginLeft: '-50vw',
      padding: '0 clamp(16px, 4vw, 64px)', boxSizing: 'border-box',
    }}>
      {shelves.map((shelf, si) => (
        <div key={si} className="book-row">
          {shelf.map(v => {
            const logo = logoFor(v.code);
            return (
              <a key={v.id} href={urlFor(v)} target="_blank" rel="noopener noreferrer" className="book-panel" style={{ textDecoration: 'none' }}>
                <SafeImage src={images[v.id]} alt={v.name} />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(0deg, rgba(0,10,12,0.88) 0%, ${v.color}55 45%, rgba(0,10,12,0.35) 100%)`,
                }} />
                {/* Default label — horizontal, visible while the panel is at rest.
                    Logo sits above the name now instead of the name standing alone. */}
                <div className="book-label-horizontal" style={{
                  position: 'absolute', left: 0, right: 0, bottom: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                  transition: 'opacity 0.25s ease', padding: '0 10px',
                }}>
                  {logo && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <img src={logo} alt={`${v.name} logo`} style={{ width: 86, height: 75, objectFit: 'contain', filter: 'drop-shadow(0 8px 48px rgb(255, 255, 255))' }} />
                    </div>
                  )}
                  <span style={{
                    fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 14, letterSpacing: '1px',
                    color: '#fff', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{v.name}</span>
                </div>

                {/* Collapsed label — vertical text, shown only once a sibling panel is being hovered and this one narrows */}
                <div className="book-label-collapsed" style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                  paddingBottom: 22, opacity: 0, transition: 'opacity 0.3s ease',
                }}>
                  <span style={{
                    writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                    fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 15, letterSpacing: '2px',
                    color: '#fff', textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>{v.code}</span>
                </div>

                {/* Expanded detail — fades in once the panel widens on hover.
                    Logo replaces the lucide icon badge, and all text is sized up
                    a step so it holds its own once the panel is this much wider. */}
                <div className="book-detail" style={{
                  position: 'absolute', left: 0, right: 0, bottom: 0, padding: 28,
                  opacity: 0, transition: 'opacity 0.35s ease 0.1s',
                }}>
                  <span style={{
                    fontSize: 13, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.18)',
                    padding: '4px 11px', borderRadius: 7, letterSpacing: '0.5px', fontFamily: 'Poppins, sans-serif',
                  }}>{v.code}</span>
                  <h3 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 24, color: '#fff', margin: '12px 0 8px', whiteSpace: 'nowrap' }}>{v.name}</h3>
                  <p style={{ fontSize: 15.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.65, maxWidth: 320, marginBottom: 16 }}>
                    {v.description.slice(0, 300)}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#fff', fontWeight: 700, fontSize: 15.5, whiteSpace: 'nowrap' }}>
                    Learn More <ArrowRight size={17} />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      ))}
      <style>{`@media (max-width: 700px) { .book-row { height: 320px; } }`}</style>
    </div>
  );
};

export default Bookshelf;